import React from 'react';
import { Toaster } from 'sonner';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from '@/contexts/AuthContext';
import { NotificationsProvider } from '@/contexts/NotificationsContext';
import Notifications from '@/pages/Notifications';
import ProtectedRoute from '@/components/ProtectedRoute';
import Navbar from '@/components/Navbar';
import Landing from '@/pages/Landing';
import RoleChoice from '@/pages/RoleChoice';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import CandidateLogin from '@/pages/CandidateLogin';
import CandidateRegister from '@/pages/CandidateRegister';
import CandidateProfile from '@/pages/CandidateProfile';
import CVBuilder from '@/pages/CVBuilder';
import Dashboard from '@/pages/Dashboard';
import CreateJob from '@/pages/CreateJob';
import EditJob from '@/pages/EditJob';
import MyJobs from '@/pages/MyJobs';
import SelectSponsorship from '@/pages/SelectSponsorship';
import Checkout from '@/pages/Checkout';
import PurchaseHistory from '@/pages/PurchaseHistory';
import AdminPurchases from '@/pages/AdminPurchases';
import AdminJobs from '@/pages/AdminJobs';
import AdminJobsOverview from '@/pages/AdminJobsOverview';
import AdminDashboard from '@/pages/AdminDashboard';
import AdminCategories from '@/pages/AdminCategories';
import AdminDrafts from '@/pages/AdminDrafts';
import AdminCommerce from '@/pages/AdminCommerce';
import VerifyEmail from '@/pages/VerifyEmail';
import AdminProspects from '@/pages/AdminProspects';
import AdminEvents from '@/pages/AdminEvents';
import AdminEventDetail from '@/pages/AdminEventDetail';
import EventInvitation from '@/pages/EventInvitation';
import AdminTrusted from '@/pages/AdminTrusted';
import AdminAI from '@/pages/AdminAI';
import AdminVideos from '@/pages/AdminVideos';
import AdminInstagramKit from '@/pages/AdminInstagramKit';
import { AffiliateRedirect } from '@/components/AffiliateRedirect';
import ForgotPassword from '@/pages/ForgotPassword';
import TrialAccept from '@/pages/TrialAccept';
import AdminAmbassadors from '@/pages/AdminAmbassadors';
import AdminCompanies from '@/pages/AdminCompanies';
import ResetPassword from '@/pages/ResetPassword';
import JobDetails from '@/pages/JobDetails';
import MyApplications from '@/pages/MyApplications';
import EmployerApplications from '@/pages/EmployerApplications';
import EmployerInterviews from '@/pages/EmployerInterviews';
import InterviewSchedule from '@/pages/InterviewSchedule';
import ProspectMeetingReports from '@/pages/ProspectMeetingReports';
import MeetingAgenda from '@/pages/MeetingAgenda';
import JobSearch from '@/pages/JobSearch';
import LogosDownload from '@/pages/LogosDownload';
import InstagramTemplatesPage from '@/pages/InstagramTemplatesPage';
import FlyersPage from '@/pages/FlyersPage';
import PublicPayment from '@/pages/PublicPayment';
import PaymentResult from '@/pages/PaymentResult';
import Favourites from '@/pages/Favourites';
import EmployerProfile from '@/pages/EmployerProfile';
import EmployerLogoUpload from '@/pages/EmployerLogoUpload';
import Unsubscribe from '@/pages/Unsubscribe';
import AdminInfluencers from '@/pages/AdminInfluencers';
import InfluencerProposal from '@/pages/InfluencerProposal';
import ReferralCapture from '@/components/ReferralCapture';
import DocumentViewer from '@/pages/DocumentViewer';
import SeoJobsPage from '@/pages/SeoJobsPage';
import Termos from '@/pages/legal/Termos';
import Privacidade from '@/pages/legal/Privacidade';
import CookiesPage from '@/pages/legal/Cookies';
import Sobre from '@/pages/legal/Sobre';
import Contacto from '@/pages/legal/Contacto';
import PoliticaDevolucoes from '@/pages/legal/PoliticaDevolucoes';
import Talento from '@/pages/Talento';
import EmpresasInternacionais from '@/pages/EmpresasInternacionais';
import Footer from '@/components/Footer';
import ChatWidget from '@/components/ChatWidget';
import IOSInstallModal from '@/components/IOSInstallModal';
import MobileInstallBanner from '@/components/MobileInstallBanner';
import { PWAProvider } from '@/contexts/PWAContext';
import AdminLayout from '@/layouts/AdminLayout';
import InstalarPWA from '@/pages/InstalarPWA';
import '@/i18n/config';
import '@/App.css';
import { useAuth } from '@/contexts/AuthContext';
import { usePushSubscription } from '@/hooks/usePushSubscription';

/** Componente invisível que activa push subscriptions após autenticação */
function PushNotificationInit() {
  const { user } = useAuth();
  usePushSubscription(!!user);
  return null;
}

function FooterConditional() {
  const { pathname } = useLocation();
  if (pathname.startsWith('/admin')) return null;
  return <Footer />;
}

function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
      <BrowserRouter>
        <PWAProvider>
        <NotificationsProvider>
        <div className="App min-h-screen bg-gray-50 flex flex-col overflow-x-hidden w-full">
          <Toaster position="top-center" richColors closeButton />
          <PushNotificationInit />
          <MobileInstallBanner />
          <Navbar />
          <ReferralCapture />
          <IOSInstallModal />
          <div className="flex-grow">
            <Routes>
            {/* Rotas públicas */}
            <Route path="/" element={<Landing />} />
            <Route path="/start" element={<RoleChoice />} />
            <Route path="/instalar" element={<InstalarPWA />} />
            <Route path="/jobs" element={<Navigate to="/jobs/search" replace />} />
            <Route path="/jobs/search" element={<JobSearch />} />
            <Route path="/jobs/:id" element={<JobDetails />} />
            <Route path="/my-applications" element={<MyApplications />} />
            <Route path="/candidate/applications" element={<MyApplications />} />
            <Route path="/logos" element={<LogosDownload />} />
            <Route path="/instagram" element={<InstagramTemplatesPage />} />
            <Route path="/flyers" element={<ProtectedRoute><FlyersPage /></ProtectedRoute>} />
            <Route path="/trial/aceitar/:token" element={<TrialAccept />} />
            <Route path="/pay/:invoice" element={<PublicPayment />} />
            <Route path="/payment-result" element={<PaymentResult />} />
            <Route path="/favoritos" element={<ProtectedRoute><Favourites /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
            <Route path="/empresa/logo" element={<ProtectedRoute><EmployerLogoUpload /></ProtectedRoute>} />
            <Route path="/empresa/:slug" element={<EmployerProfile />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/parceiro/:token" element={<InfluencerProposal />} />
            <Route path="/documento" element={<DocumentViewer />} />
            <Route path="/empregos/:sector" element={<SeoJobsPage />} />
            <Route path="/empregos/:sector/:island" element={<SeoJobsPage />} />
            <Route path="/evento/convite" element={<EventInvitation />} />
            <Route path="/verify-email" element={<VerifyEmail />} />

            {/* Legal */}
            <Route path="/termos" element={<Termos />} />
            <Route path="/privacidade" element={<Privacidade />} />
            <Route path="/cookies" element={<CookiesPage />} />
            <Route path="/sobre" element={<Sobre />} />
            <Route path="/contacto" element={<Contacto />} />
            <Route path="/politica-devolucoes" element={<PoliticaDevolucoes />} />
            <Route path="/devolucoes" element={<Navigate to="/politica-devolucoes" replace />} />
            <Route path="/talento-em-cabo-verde" element={<Talento />} />
            <Route path="/empresas-internacionais" element={<EmpresasInternacionais />} />
            <Route path="/talento" element={<Navigate to="/talento-em-cabo-verde" replace />} />

            {/* Empregador */}
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/register" element={<Register />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/create-job" element={<ProtectedRoute><CreateJob /></ProtectedRoute>} />
            <Route path="/edit-job/:id" element={<ProtectedRoute><EditJob /></ProtectedRoute>} />
            <Route path="/my-jobs" element={<ProtectedRoute><MyJobs /></ProtectedRoute>} />
            <Route path="/select-sponsorship/:jobId" element={<ProtectedRoute><SelectSponsorship /></ProtectedRoute>} />
            <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
            <Route path="/checkout/stripe-return" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
            <Route path="/applications" element={<ProtectedRoute><EmployerApplications /></ProtectedRoute>} />
            <Route path="/interviews" element={<ProtectedRoute><EmployerInterviews /></ProtectedRoute>} />
            <Route path="/entrevista/:token" element={<InterviewSchedule />} />
            <Route path="/purchases" element={<ProtectedRoute><PurchaseHistory /></ProtectedRoute>} />

            {/* Candidato */}
            <Route path="/candidate/login" element={<CandidateLogin />} />
            <Route path="/candidate/register" element={<CandidateRegister />} />
            <Route path="/candidate/profile" element={<ProtectedRoute><CandidateProfile /></ProtectedRoute>} />
            <Route path="/candidate/cv" element={<ProtectedRoute><CVBuilder /></ProtectedRoute>} />

            {/* Admin — menu lateral via AdminLayout */}
            <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="jobs" element={<AdminJobs />} />
              <Route path="jobs/overview" element={<AdminJobsOverview />} />
              <Route path="drafts" element={<AdminDrafts />} />
              <Route path="companies" element={<AdminCompanies />} />
              <Route path="categories" element={<AdminCategories />} />
              <Route path="purchases" element={<AdminPurchases />} />
              <Route path="prospects" element={<AdminProspects />} />
              <Route path="prospects/agenda" element={<MeetingAgenda />} />
              <Route path="prospects/:pid/meetings" element={<ProspectMeetingReports />} />
              <Route path="automations" element={<Navigate to="/admin" replace />} />
              <Route path="ambassadors" element={<AdminAmbassadors />} />
              <Route path="influencers" element={<AdminInfluencers />} />
              <Route path="events" element={<AdminEvents />} />
              <Route path="events/:id" element={<AdminEventDetail />} />
              <Route path="trusted" element={<AdminTrusted />} />
              <Route path="commerce" element={<AdminCommerce />} />
              <Route path="ai" element={<AdminAI />} />
              <Route path="videos" element={<AdminVideos />} />
              <Route path="promo-videos" element={<Navigate to="/admin" replace />} />
              <Route path="content-studio" element={<Navigate to="/admin" replace />} />
              <Route path="content-studio/spinguera" element={<Navigate to="/admin" replace />} />
              <Route path="instagram-kit" element={<AdminInstagramKit />} />
              <Route path="tips-bank" element={<Navigate to="/admin" replace />} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<AffiliateRedirect />} />
          </Routes>
          </div>
          <FooterConditional />
          <ChatWidget />
        </div>
        </NotificationsProvider>
        </PWAProvider>
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
